import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Search, Download, Users, Heart, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FamiliariAssistiti() {
  const [familiari, setFamiliari] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const [pdfOrdinamento, setPdfOrdinamento] = useState('familiare');
  const [pdfMostraSocio, setPdfMostraSocio] = useState(true);
  const navigate = useNavigate();

  const fetchFamiliari = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await fetch(`${API_URL}/api/soci/familiari-assistiti?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setFamiliari(data.familiari || []);
        setCurrentPage(1);
      } else {
        toast.error('Errore nel caricamento dei dati');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchFamiliari();
  }, [fetchFamiliari]);

  const handleExportCSV = () => {
    if (familiari.length === 0) {
      toast.error('Nessun dato da esportare');
      return;
    }

    const headers = ['Familiare/Assistito', 'Socio di Riferimento', 'Tessera', 'Tipo Socio', 'Stato', 'Codice Fiscale Socio', 'Email Socio', 'Telefono Socio', 'Data Iscrizione'];
    const rows = familiari.map(f => [
      f.familiare_collegato,
      f.socio_nome,
      f.socio_tessera,
      f.socio_tipo,
      f.socio_stato,
      f.socio_codice_fiscale,
      f.socio_email,
      f.socio_telefono,
      f.data_iscrizione
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(r => r.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `familiari_assistiti_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportati ${familiari.length} record`);
  };

  const handleExportPDF = async () => {
    if (familiari.length === 0) {
      toast.error('Nessun dato da esportare');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('ordinamento', pdfOrdinamento);
      params.append('mostra_socio', pdfMostraSocio);
      
      const response = await fetch(`${API_URL}/api/soci/familiari-assistiti/pdf?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `familiari_assistiti_${new Date().toISOString().slice(0, 10)}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('PDF generato con successo');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Errore nella generazione del PDF');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const paginatedData = familiari.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(familiari.length / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6" data-testid="familiari-assistiti-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500" />
            Familiari e Assistiti
          </h1>
          <p className="text-sm text-slate-500 mt-1">Elenco familiari/assistiti collegati ai soci</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Esporta
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="px-3 py-2 space-y-3">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Esporta PDF
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Ordina per:</Label>
                  <select 
                    className="text-xs border rounded px-2 py-1 flex-1"
                    value={pdfOrdinamento}
                    onChange={(e) => setPdfOrdinamento(e.target.value)}
                    data-testid="pdf-ordinamento"
                  >
                    <option value="familiare">Familiare (A-Z)</option>
                    <option value="socio">Socio (A-Z)</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="mostra-socio" 
                    checked={pdfMostraSocio} 
                    onCheckedChange={setPdfMostraSocio}
                    data-testid="pdf-mostra-socio"
                  />
                  <Label htmlFor="mostra-socio" className="text-xs">Mostra colonna Socio Associato</Label>
                </div>
              </div>
              <Button size="sm" className="w-full" onClick={handleExportPDF} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                Genera PDF
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{familiari.length}</p>
              <p className="text-xs text-slate-500">Familiari/Assistiti Collegati</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {new Set(familiari.map(f => f.persona_id)).size}
              </p>
              <p className="text-xs text-slate-500">Soci con Familiare Collegato</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome familiare, socio, codice fiscale, tessera..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchFamiliari()}
                data-testid="search-familiari"
              />
            </div>
            <Button variant="outline" onClick={fetchFamiliari} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : familiari.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Heart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Nessun familiare/assistito collegato</p>
              <p className="text-sm mt-1">I familiari vengono collegati durante la registrazione del socio nel campo "Familiare Collegato"</p>
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Familiare / Assistito</TableHead>
                  <TableHead>Socio di Riferimento</TableHead>
                  <TableHead className="hidden md:table-cell">Tessera</TableHead>
                  <TableHead className="hidden md:table-cell">Tipo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="hidden lg:table-cell">Email Socio</TableHead>
                  <TableHead className="hidden lg:table-cell">Iscrizione</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((f, idx) => (
                  <TableRow
                    key={`${f.persona_id}-${idx}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/soci/${f.persona_id}`)}
                    data-testid={`familiare-row-${idx}`}
                  >
                    <TableCell className="font-semibold text-rose-700">
                      {f.familiare_collegato}
                    </TableCell>
                    <TableCell>{f.socio_nome}</TableCell>
                    <TableCell className="hidden md:table-cell font-mono">
                      {f.socio_tessera || '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="outline" className="text-xs">
                        Familiare
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        f.socio_stato === 'attivo' ? 'bg-green-100 text-green-700' :
                        f.socio_stato === 'sospeso' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {f.socio_stato}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                      {f.socio_email || '-'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                      {f.data_iscrizione || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {familiari.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-slate-500">
                  Pagina {currentPage} di {totalPages} — {familiari.length} record totali
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    Precedente
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Successiva
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
